import { useId, useState } from 'react';
import { Plus, X } from 'lucide-react';
import { postService } from '@/services/api';
import { validateImageFile } from '@/lib/storage';
import { UserAvatar } from '@/components/ui/Avatar';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/contexts/ToastContext';

interface PostComposerProps {
  onPostCreated?: (content: string | null) => void;
  initialContent?: string | null;
  quotedPostId?: string | null;
}

export default function PostComposer({ onPostCreated, initialContent, quotedPostId }: PostComposerProps) {
  const { user, profile } = useAuth();
  const { toast } = useToast();
  const [content, setContent] = useState(initialContent || '');
  const [media, setMedia] = useState<File[]>([]);
  const [previewUrls, setPreviewUrls] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);
  const mediaInputId = useId();

  const handleMediaChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const valid: File[] = [];
    const urls: string[] = [];

    for (const file of files) {
      const validation = validateImageFile(file);
      if (!validation.valid) {
        toast('Invalid file', validation.error, 'destructive');
        continue;
      }
      valid.push(file);
      urls.push(URL.createObjectURL(file));
    }

    setMedia((prev) => [...prev, ...valid].slice(0, 4));
    setPreviewUrls((prev) => [...prev, ...urls].slice(0, 4));
  };

  const removeMedia = (index: number) => {
    setMedia((prev) => prev.filter((_, i) => i !== index));
    setPreviewUrls((prev) => prev.filter((_, i) => i !== index));
    if (previewUrls[index]) URL.revokeObjectURL(previewUrls[index]);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!content.trim() && media.length === 0) return;
    setUploading(true);
    try {
      const { error } = await postService.createPost(content.trim() || null, media, {
        quotedPostId: quotedPostId || null,
      });
      if (error) {
        toast('Failed to post', error.message, 'destructive');
        return;
      }
      setContent('');
      setMedia([]);
      setPreviewUrls([]);
      previewUrls.forEach((u) => URL.revokeObjectURL(u));
      toast('Post shared');
      onPostCreated?.(content.trim() || null);
    } catch (err: any) {
      toast('Failed to post', err.message, 'destructive');
    } finally {
      setUploading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="post-card p-4">
      <div className="flex gap-3">
        {user && profile && <UserAvatar src={profile.avatar_url} size="md" />}
        <div className="min-w-0 flex-1">
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="What's happening?"
            className="w-full bg-transparent border-none resize-none focus:ring-0 text-neutral-900 dark:text-neutral-100 text-sm leading-relaxed min-h-[80px] placeholder:text-neutral-400 dark:placeholder:text-neutral-500"
            rows={3}
          />
          <div className="relative mt-2 pr-3 flex justify-end items-center">
            <button
              type="button"
              onClick={() => document.getElementById(mediaInputId)?.click()}
              className="absolute left-0 top-1/2 -translate-y-1/2 p-2 rounded-lg text-neutral-400 hover:text-brand-600 dark:hover:text-brand-400 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors" aria-label="Add media"
            >
              <Plus size={18} />
            </button>
            <button
              type="submit"
              disabled={uploading || (!content.trim() && media.length === 0)}
              className="btn-primary"
            >
              Post
            </button>
          </div>
        </div>
      </div>

      {previewUrls.length > 0 && (
        <div className="mt-3 flex gap-2 overflow-x-auto pb-1">
          {previewUrls.map((url, i) => (
            <div key={i} className="relative flex-shrink-0 w-24 h-24 rounded-xl overflow-hidden">
              <img src={url} alt="Preview" className="object-cover w-full h-full" />
              <button onClick={() => removeMedia(i)} className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-1">
                <X size={14} />
              </button>
            </div>
          ))}
        </div>
      )}

      <input
        id={mediaInputId}
        type="file"
        accept="image/*"
        multiple
        onChange={handleMediaChange}
        className="hidden"
      />
    </form>
  );
}
